import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import TopBannerGuidance from '../../../components/TopBannerGuidance';
import OwlMascot from '../../../assets/貓頭鷹博士_雙手.gif';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';
import {
  Book as BookIcon,
  Person as PersonIcon,
  School as ClassIcon,
  Settings as PrepSettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Home as PrepHomeIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PlayCircle as PlayCircleIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Menu,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse
} from '@mui/material';

export default function CourseDispatchManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [academicYearFilter, setAcademicYearFilter] = useState('全部');
  const [openDispatchDialog, setOpenDispatchDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dispatchData, setDispatchData] = useState(null);
  const [openGroupManagementDialog, setOpenGroupManagementDialog] = useState(false);
  const [selectedDispatchRecord, setSelectedDispatchRecord] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [courseDispatches, setCourseDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOwlGuidance, setShowOwlGuidance] = useState(false); // 初始為 false，資料載入完成後再顯示
  const [courseGroupsInfo, setCourseGroupsInfo] = useState({}); // 儲存每個課程的分組資訊
  const [dataLoaded, setDataLoaded] = useState(false); // 標記資料是否已載入完成

  // 獲取課程派發資料
  const fetchCourseDispatches = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(config.getCourseDispatches);
      if (response.data.success) {
        const dispatches = response.data.data;
        setCourseDispatches(dispatches);
        
        // 並行獲取每個課程的分組資訊
        const groupsInfoPromises = dispatches.map(async (dispatch) => {
          try {
            const groupsResponse = await axiosInstance.get(`${config.getCourseDispatchGroups}${dispatch.id}/groups`);
            if (groupsResponse.data && groupsResponse.data.success) {
              const groups = groupsResponse.data.data || [];
              return {
                courseId: dispatch.id,
                hasGroups: groups.length > 0,
                groupCount: groups.length
              };
            }
          } catch (error) {
            console.error(`獲取課程 ${dispatch.id} 分組資訊錯誤:`, error);
          }
          return {
            courseId: dispatch.id,
            hasGroups: false,
            groupCount: 0
          };
        });
        
        const groupsInfoResults = await Promise.all(groupsInfoPromises);
        const groupsInfoMap = {};
        groupsInfoResults.forEach(info => {
          if (info) {
            groupsInfoMap[info.courseId] = {
              hasGroups: info.hasGroups,
              groupCount: info.groupCount
            };
          }
        });
        setCourseGroupsInfo(groupsInfoMap);
        setDataLoaded(true); // 標記資料已載入完成
      } else {
        setSnackbar({ open: true, message: '獲取課程派發資料失敗', severity: 'error' });
        setDataLoaded(true);
      }
    } catch (error) {
      console.error('獲取課程派發資料錯誤:', error);
      setSnackbar({ open: true, message: '獲取課程派發資料失敗', severity: 'error' });
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // 處理從備課包管理傳來的資料
  useEffect(() => {
    if (location.state) {
      setDispatchData(location.state);
      setOpenDispatchDialog(true);
      // 清除 location.state 以避免重複觸發
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  // 初始化載入資料
  useEffect(() => {
    fetchCourseDispatches();
  }, []);

  // 當資料載入完成後，顯示小提醒
  useEffect(() => {
    if (dataLoaded && !loading) {
      setShowOwlGuidance(true);
    }
  }, [dataLoaded, loading]);

  // 處理選單開啟
  const handleMenuOpen = (event, recordId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRecordId(recordId);
  };

  // 處理選單關閉
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecordId(null);
  };

  // 處理刪除確認
  const handleDeleteConfirm = async () => {
    if (selectedRecordId) {
      const record = courseDispatches.find(r => r.id === selectedRecordId);
      if (record && window.confirm(`確定要刪除課程「${record.title}」嗎？\n\n此操作將刪除：\n- 課程的所有分組\n- 課程的所有任務\n\n此操作無法復原！`)) {
        try {
          const response = await axiosInstance.delete(`${config.deleteCourseDispatch}${record.id}`);
          if (response.data.success) {
            setSnackbar({ open: true, message: '課程已刪除', severity: 'success' });
            fetchCourseDispatches(); // 重新載入資料
            // 清理狀態
            setSelectedRecordId(null);
          } else {
            setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
          }
        } catch (error) {
          console.error('刪除課程錯誤:', error);
          setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
        }
      }
    }
    handleMenuClose();
  };

  // 處理課程狀態變更
  const handleStatusChange = async (newStatus) => {
    if (selectedRecordId) {
      const record = courseDispatches.find(r => r.id === selectedRecordId);
      try {
        const response = await axiosInstance.put(`${config.updateCourseDispatchStatus}${selectedRecordId}/status`, {
          status: newStatus
        });
        if (response.data.success) {
          const statusText = newStatus === 'active' ? '進行中' : newStatus === 'completed' ? '已完成' : '已暫停';
          setSnackbar({ open: true, message: `課程狀態已更新為：${statusText}`, severity: 'success' });
          fetchCourseDispatches(); // 重新載入資料
        } else {
          setSnackbar({ open: true, message: '狀態更新失敗', severity: 'error' });
        }
      } catch (error) {
        console.error('更新課程狀態錯誤:', error);
        setSnackbar({ open: true, message: '狀態更新失敗', severity: 'error' });
      }
    }
    handleMenuClose();
  };

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('preparation', 'course-dispatch', navigate);

  // 學年度選項（112~116）
  const academicYears = React.useMemo(() => {
    return ['全部', '116學年度', '115學年度', '114學年度', '113學年度', '112學年度'];
  }, []);

  const filteredRecords = courseDispatches.filter(record => {
    const matchesSearch = record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (record.Class && record.Class.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (record.LessonPackage && record.LessonPackage.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesAcademicYear = academicYearFilter === '全部' || 
                               (record.Class && record.Class.year === academicYearFilter);
    
    return matchesSearch && matchesAcademicYear;
  });


  const getStatusIcon = (status) => {
    switch (status) {
      case '已完成': return <CheckCircleIcon />;
      case '進行中': return <PlayCircleIcon />;
      case '草稿': return <ScheduleIcon />;
      default: return <ScheduleIcon />;
    }
  };

  return (
    <div className="teacher-layout course-dispatch-management-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="preparation"
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">課程派發管理</h1>
          {/* 篩選區域在標題下方置中 */}
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋派發記錄"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
          />
          <FormControl 
            size="small" 
            className="filter-select" 
            sx={{ 
              minWidth: 120, 
              maxWidth: 150,
              width: 'auto',
              flexShrink: 0
            }}
          >
            <InputLabel shrink>學年</InputLabel>
            <Select
              value={academicYearFilter}
              onChange={(e) => setAcademicYearFilter(e.target.value)}
              label="學年"
              sx={{ 
                height: '40px',
                '& .MuiSelect-select': {
                  padding: '8.5px 14px',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            >
              {academicYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <button
            className="btn-secondary filter-clear-btn"
            onClick={() => {
              setSearchTerm('');
              setAcademicYearFilter('全部');
            }}
          >
            清除篩選
          </button>
          </div>
        </div>

        {/* 頂部橫幅提醒 - 只在資料載入完成且 showOwlGuidance 為 true 時顯示 */}
        {dataLoaded && showOwlGuidance && (
          <TopBannerGuidance
            show={showOwlGuidance}
            onClose={() => setShowOwlGuidance(false)}
            severity={(() => {
              if (courseDispatches.length === 0) return 'info';
              const coursesWithoutGroups = courseDispatches.filter(dispatch => {
                const info = courseGroupsInfo[dispatch.id];
                return !info || !info.hasGroups;
              }).length;
              return coursesWithoutGroups === 0 ? 'success' : 'warning';
            })()}
            mascotImage={OwlMascot}
            message={(() => {
              if (courseDispatches.length === 0) {
                return '選擇備課包和班級來派發課程。派發後，記得點擊「分組管理」為課程設定專屬小組，然後就可以前往「課中實施」開始教學活動了。';
              }
              
              // 檢查有多少課程還沒有設定分組
              const coursesWithoutGroups = courseDispatches.filter(dispatch => {
                const info = courseGroupsInfo[dispatch.id];
                return !info || !info.hasGroups;
              }).length;
              
              if (coursesWithoutGroups > 0) {
                return `目前有 ${courseDispatches.length} 個課程派發，其中 ${coursesWithoutGroups} 個課程還沒有設定專屬小組。記得點擊「分組管理」按鈕，為每個課程設定小組，這樣才能順利指派任務給學生。`;
              }
              
              return `很好！目前有 ${courseDispatches.length} 個課程派發，且都已設定好專屬小組。現在可以前往「課中實施」查看課程，並開始指派任務給學生了。`;
            })()}
          />
        )}

        {/* 派發記錄列表 */}
        <div className="activity-card-list grid-responsive">
          {/* 新增派發課程卡片 */}
          <div
            className="activity-card add-activity"
            onClick={() => {
              setDispatchData(null); // 清除之前的資料
              setOpenDispatchDialog(true);
            }}
            style={{ 
              background: '#2b687f',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              minHeight: '200px',
              maxHeight: '200px',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.transition = 'all 0.3s ease';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.transition = 'all 0.3s ease';
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 48, mb: 1 }} />
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                新增課程
              </div>
            </div>
          </div>

          {filteredRecords.map((record) => (
            <div
              className="activity-card"
              key={record.id}
              style={{ 
                position: 'relative', 
                background:'#fff', 
                boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
                borderRadius: '12px', 
                border:'1.5px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                minHeight: '200px',
                maxHeight: '200px',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(44,44,80,0.12)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(44,44,80,0.08)';
              }}
            >
              <div className="activity-name" style={{ 
                fontSize: '1.2rem', 
                marginBottom: '8px',
                padding: '0 8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.4',
                minHeight: '2.8rem'
              }}>
                {record.title}
              </div>
              
              
              {/* 課程標籤：人數、組別數和學期 */}
              {record.stats && (
                <div style={{ 
                  display: 'flex', 
                  gap: '6px', 
                  marginBottom: '8px',
                  padding: '0 8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    backgroundColor: '#e8f5e8',
                    color: '#2e7d32',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}>
                    {record.stats.totalStudents} 人
                  </span>
                  <span style={{
                    backgroundColor: '#f3e5f5',
                    color: '#7b1fa2',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}>
                    {record.stats.totalGroups} 組
                  </span>
                  {record.Class && record.Class.semester && (
                    <span style={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '0.7rem',
                      fontWeight: '500'
                    }}>
                      {record.Class.year && `${record.Class.year} `}{record.Class.semester}
                    </span>
                  )}
                </div>
              )}
              
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#666', 
                marginTop: '2px',
                padding: '0 8px'
              }}>
                <span>建立日期: {new Date(record.createdAt).toLocaleDateString()}</span>
              </div>

              {/* 主要功能按鈕 */}
              <div style={{ 
                display: 'flex', 
                gap: '6px', 
                justifyContent: 'center',
                marginTop: '8px',
                padding: '0 8px'
              }}>
                <button
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    // 跳轉到任務管理頁面
                    navigate(`/teacher/preparation/task-management/${record.id}`);
                  }}
                >
                  任務管理
                </button>
                <button
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDispatchRecord(record);
                    setOpenGroupManagementDialog(true);
                  }}
                >
                  分組管理
                </button>
              </div>

              {/* 更多選單按鈕 */}
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, record.id)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#666',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.05)'
                  },
                  zIndex: 1
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>

        {/* 更多選單 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* 暫時註解課程狀態選單項 */}
          {/* <MenuItem onClick={() => handleStatusChange('active')}>
            <PlayCircleIcon sx={{ mr: 1, color: '#4caf50' }} />
            設為進行中
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('paused')}>
            <ScheduleIcon sx={{ mr: 1, color: '#ff9800' }} />
            設為已暫停
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('completed')}>
            <CheckCircleIcon sx={{ mr: 1, color: '#9e9e9e' }} />
            設為已完成
          </MenuItem>
          <MenuItem onClick={handleDeleteConfirm} sx={{ color: '#f44336' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            刪除課程
          </MenuItem> */}
        </Menu>

        {/* 派發課程對話框 */}
        <DispatchCourseDialog
          open={openDispatchDialog}
          onClose={() => {
            setOpenDispatchDialog(false);
            setDispatchData(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            setOpenDispatchDialog(false);
            setDispatchData(null);
            // 重新載入課程派發資料
            fetchCourseDispatches();
          }}
          initialData={dispatchData}
        />

        {/* 分組管理對話框 */}
        <GroupManagementDialog
          open={openGroupManagementDialog}
          dispatchRecord={selectedDispatchRecord}
          onClose={() => {
            const currentRecord = selectedDispatchRecord;
            setOpenGroupManagementDialog(false);
            setSelectedDispatchRecord(null);
            // 關閉對話框後重新獲取分組資訊
            if (currentRecord) {
              fetchCourseDispatches();
            }
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            // 分組更新後重新獲取分組資訊
            fetchCourseDispatches();
            // 不要關閉對話框，讓用戶可以繼續管理
            // setOpenGroupManagementDialog(false);
            // setSelectedDispatchRecord(null);
          }}
        />

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

// 派發課程對話框組件
function DispatchCourseDialog({ open, onClose, onSuccess, initialData }) {
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('114學年度');
  const [selectedCommunities, setSelectedCommunities] = useState([]);
  const [selectedLessonPackages, setSelectedLessonPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [lessonPackages, setLessonPackages] = useState([]);
  const [packageTasks, setPackageTasks] = useState({}); // { packageId: { tasks: [], groupedByUnit: {} } }
  const [selectedUnits, setSelectedUnits] = useState({}); // { packageId: { unitNumber: [subtaskNumbers] } }
  const [expandedPackages, setExpandedPackages] = useState({}); // 展開的備課包ID列表

  // 獲取社群資料
  const fetchCommunities = async () => {
    try {
      console.log('正在獲取社群資料，API 端點:', config.getClasses);
      const response = await axiosInstance.get(config.getClasses);
      console.log('社群資料回應:', response.data);
      if (response.data) {
        setCommunities(response.data);
        console.log('已設定社群資料:', response.data);
      } else {
        console.warn('社群資料回應為空');
        setCommunities([]);
      }
    } catch (error) {
      console.error('獲取社群資料錯誤:', error);
      console.error('錯誤詳情:', error.response?.data);
      setCommunities([]);
    }
  };

  // 獲取備課包資料
  const fetchLessonPackages = async () => {
    try {
      const response = await axiosInstance.get(config.getLessonPackages);
      if (response.data.success) {
        const packages = response.data.data;
        setLessonPackages(packages);
        
        // 並行獲取每個備課包的任務資料
        const taskPromises = packages.map(async (pkg) => {
          try {
            const taskResponse = await axiosInstance.get(`${config.getLessonPackages}/${pkg.id}`);
            if (taskResponse.data.success) {
              const tasks = taskResponse.data.data?.LessonPackageTasks || [];
              // 按章節分組
              const groupedByUnit = {};
              tasks.forEach(task => {
                const unitNumber = task.unit_number || 1;
                if (!groupedByUnit[unitNumber]) {
                  groupedByUnit[unitNumber] = [];
                }
                groupedByUnit[unitNumber].push(task);
              });
              // 對每個章節的任務排序
              Object.keys(groupedByUnit).forEach(unitNum => {
                groupedByUnit[unitNum].sort((a, b) => {
                  if (a.subtask_number && b.subtask_number) {
                    return a.subtask_number.localeCompare(b.subtask_number);
                  }
                  return (a.order_index || 0) - (b.order_index || 0);
                });
              });
              return {
                packageId: pkg.id,
                tasks,
                groupedByUnit
              };
            }
          } catch (error) {
            console.error(`獲取備課包 ${pkg.id} 任務錯誤:`, error);
          }
          return {
            packageId: pkg.id,
            tasks: [],
            groupedByUnit: {}
          };
        });
        
        const taskResults = await Promise.all(taskPromises);
        const tasksMap = {};
        taskResults.forEach(result => {
          tasksMap[result.packageId] = result;
        });
        setPackageTasks(tasksMap);
      }
    } catch (error) {
      console.error('獲取備課包資料錯誤:', error);
    }
  };

  // 處理初始資料
  useEffect(() => {
    if (initialData && initialData.lessonPackage) {
      // 如果從備課包管理跳轉，自動選中該備課包
      const lessonPackage = initialData.lessonPackage;
      setSelectedLessonPackages([lessonPackage.id]);
    }
  }, [initialData]);

  // 載入資料
  useEffect(() => {
    if (open) {
      fetchCommunities();
      fetchLessonPackages();
    }
  }, [open]);

  // 對話框關閉時重置狀態
  useEffect(() => {
    if (!open) {
      setSelectedAcademicYear('114學年度');
      setSelectedCommunities([]);
      setSelectedLessonPackages([]);
    }
  }, [open]);

  // 學年度選項（112~116）
  const academicYears = React.useMemo(() => {
    return ['全部', '116學年度', '115學年度', '114學年度', '113學年度', '112學年度'];
  }, []);

  const filteredCommunities = communities.filter(community => {
    // 如果學年度是"全部"，顯示所有社群
    if (selectedAcademicYear === '全部') {
      return true;
    }
    // 提取學年度數字進行比較（例如："112學年度" -> "112"）
    const selectedYear = selectedAcademicYear.replace('學年度', '');
    const communityYear = String(community.year);
    const matches = communityYear === selectedYear;
    console.log(`社群 ${community.name} (${community.year}) 與選中學年度 ${selectedYear} 匹配:`, matches);
    return matches;
  });

  // 調試信息
  console.log('所有社群:', communities);
  console.log('選中學年度:', selectedAcademicYear);
  console.log('篩選後的社群:', filteredCommunities);

  const handleCommunityToggle = (communityId) => {
    setSelectedCommunities(prev => 
      prev.includes(communityId) 
        ? prev.filter(id => id !== communityId)
        : [...prev, communityId]
    );
  };

  const handleLessonPackageToggle = (packageId) => {
    setSelectedLessonPackages(prev => {
      const isSelected = prev.includes(packageId);
      if (isSelected) {
        // 取消選擇時，清除該備課包的章節/子任務選擇
        setSelectedUnits(prevUnits => {
          const newUnits = { ...prevUnits };
          delete newUnits[packageId];
          return newUnits;
        });
        return prev.filter(id => id !== packageId);
      } else {
        // 選擇時，預設選擇所有章節的所有子任務
        const tasksData = packageTasks[packageId];
        if (tasksData && tasksData.groupedByUnit) {
          const defaultUnits = {};
          Object.keys(tasksData.groupedByUnit).forEach(unitNum => {
            const tasks = tasksData.groupedByUnit[unitNum];
            defaultUnits[unitNum] = tasks.map(t => t.subtask_number || t.id.toString());
          });
          setSelectedUnits(prevUnits => ({
            ...prevUnits,
            [packageId]: defaultUnits
          }));
        }
        return [...prev, packageId];
      }
    });
  };

  const togglePackageExpansion = (packageId) => {
    setExpandedPackages(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  const handleUnitToggle = (packageId, unitNumber) => {
    setSelectedUnits(prev => {
      const packageUnits = prev[packageId] || {};
      const unitSubtasks = packageUnits[unitNumber] || [];
      const tasksData = packageTasks[packageId];
      const allSubtasks = tasksData?.groupedByUnit[unitNumber]?.map(t => t.subtask_number || t.id.toString()) || [];
      
      // 如果當前已全選，則取消選擇；否則全選
      const isAllSelected = allSubtasks.length > 0 && allSubtasks.every(st => unitSubtasks.includes(st));
      
      return {
        ...prev,
        [packageId]: {
          ...packageUnits,
          [unitNumber]: isAllSelected ? [] : allSubtasks
        }
      };
    });
  };

  const handleSubtaskToggle = (packageId, unitNumber, subtaskNumber) => {
    setSelectedUnits(prev => {
      const packageUnits = prev[packageId] || {};
      const unitSubtasks = packageUnits[unitNumber] || [];
      const isSelected = unitSubtasks.includes(subtaskNumber);
      
      return {
        ...prev,
        [packageId]: {
          ...packageUnits,
          [unitNumber]: isSelected
            ? unitSubtasks.filter(st => st !== subtaskNumber)
            : [...unitSubtasks, subtaskNumber]
        }
      };
    });
  };

  // 處理選單開啟
  const handleMenuOpen = (event, recordId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRecordId(recordId);
  };

  // 處理選單關閉
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecordId(null);
  };

  // 處理刪除確認
  const handleDeleteConfirm = () => {
    if (selectedRecordId && window.confirm('確定要刪除這個派發記錄嗎？')) {
      // 這裡應該調用刪除 API
      console.log('刪除派發記錄:', selectedRecordId);
      setSnackbar({ open: true, message: '派發記錄已刪除', severity: 'success' });
    }
    handleMenuClose();
  };

  const handleSubmit = async () => {
    if (selectedCommunities.length === 0 || selectedLessonPackages.length === 0) {
      alert('請選擇目標社群和備課包');
      return;
    }

    setLoading(true);
    try {
      // 使用批次建立 API
      const batchData = {
        class_ids: selectedCommunities,
        lesson_package_ids: selectedLessonPackages,
        title_template: '{class_name} - {package_name}'
      };
      
      const response = await axiosInstance.post(config.batchCreateCourseDispatches, batchData);
      
      if (response.data.success) {
        const { successful, failed } = response.data.data;
        let message = `課程派發完成！成功 ${successful.length} 個`;
        
        if (failed.length > 0) {
          message += `，失敗 ${failed.length} 個`;
          console.warn('派發失敗的項目:', failed);
          // 顯示失敗的詳細信息
          const failedDetails = failed.map(f => 
            `社群 ${f.class_id} + 備課包 ${f.lesson_package_id}: ${f.error}`
          ).join('\n');
          console.warn('失敗詳情:', failedDetails);
        }
        
        // 只有當有成功的項目時才調用 onSuccess 刷新列表
        // 如果全部失敗，不刷新列表，只顯示錯誤訊息
        if (successful.length > 0) {
          onSuccess(message);
        } else {
          alert(`所有課程派發都失敗了。\n\n失敗詳情：\n${failed.map(f => `- ${f.error}`).join('\n')}`);
        }
        
        setSelectedCommunities([]);
        setSelectedLessonPackages([]);
        setSelectedAcademicYear('114學年度');
      } else {
        throw new Error(response.data.message || '派發失敗');
      }
    } catch (error) {
      console.error('派發課程錯誤:', error);
      const errorMessage = error.response?.data?.message || error.message || '派發失敗';
      alert('派發失敗：' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>課程派發</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>

          {/* 學年度選擇 */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>學年度</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value);
                  setSelectedCommunities([]); // 清空已選社群
                }}
                label="學年度"
                required
              >
                {academicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>


          {/* 雙欄布局 */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* 左欄：備課包清單 */}
              <Grid item xs={12} md={5}>
                <Typography variant="h6" gutterBottom>備課包清單</Typography>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  maxHeight: 300, 
                  overflow: 'auto',
                  p: 1
                }}>
                  {lessonPackages.map((lessonPackage) => {
                    const tasksData = packageTasks[lessonPackage.id];
                    const isSelected = selectedLessonPackages.includes(lessonPackage.id);
                    const isExpanded = expandedPackages[lessonPackage.id];
                    const selectedUnitsForPackage = selectedUnits[lessonPackage.id] || {};
                    const groupedByUnit = tasksData?.groupedByUnit || {};
                    const unitNumbers = Object.keys(groupedByUnit).sort((a, b) => parseInt(a) - parseInt(b));
                    
                    return (
                      <Box key={lessonPackage.id} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: isSelected ? 1 : 0 }}>
                      <Checkbox
                            checked={isSelected}
                        onChange={() => handleLessonPackageToggle(lessonPackage.id)}
                      />
                      <Box sx={{ ml: 1, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {lessonPackage.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lessonPackage.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {lessonPackage.grade} - {lessonPackage.subject}
                        </Typography>
                      </Box>
                          {isSelected && unitNumbers.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => togglePackageExpansion(lessonPackage.id)}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                    </Box>
                        
                        {/* 章節/子任務選擇區域 */}
                        {isSelected && isExpanded && unitNumbers.length > 0 && (
                          <Collapse in={isExpanded}>
                            <Box sx={{ ml: 4, mt: 1, pl: 2, borderLeft: '2px solid #e3f2fd' }}>
                              <Typography variant="caption" fontWeight="bold" color="primary" display="block" sx={{ mb: 1 }}>
                                選擇要派發的章節與子任務：
                              </Typography>
                              {unitNumbers.map(unitNum => {
                                const tasks = groupedByUnit[unitNum];
                                const unitSubtasks = selectedUnitsForPackage[unitNum] || [];
                                const allSubtasks = tasks.map(t => t.subtask_number || t.id.toString());
                                const isUnitAllSelected = allSubtasks.length > 0 && allSubtasks.every(st => unitSubtasks.includes(st));
                                
                                return (
                                  <Box key={unitNum} sx={{ mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                      <Checkbox
                                        size="small"
                                        checked={isUnitAllSelected}
                                        indeterminate={unitSubtasks.length > 0 && !isUnitAllSelected}
                                        onChange={() => handleUnitToggle(lessonPackage.id, unitNum)}
                                      />
                                      <Typography variant="body2" fontWeight="600" sx={{ color: '#1976d2' }}>
                                        章節 {unitNum} ({tasks.length} 個任務)
                                      </Typography>
                                    </Box>
                                    <Box sx={{ ml: 4 }}>
                                      {tasks.map(task => {
                                        const subtaskKey = task.subtask_number || task.id.toString();
                                        const isSubtaskSelected = unitSubtasks.includes(subtaskKey);
                                        return (
                                          <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            <Checkbox
                                              size="small"
                                              checked={isSubtaskSelected}
                                              onChange={() => handleSubtaskToggle(lessonPackage.id, unitNum, subtaskKey)}
                                            />
                                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                                              {task.subtask_number ? `子任務 ${task.subtask_number}` : `任務 ${task.order_index || ''}`}: {task.title || '未命名任務'}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* 中間：箭頭 */}
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SendIcon sx={{ fontSize: 40, color: '#2b687f' }} />
              </Grid>

              {/* 右欄：目標社群 */}
              <Grid item xs={12} md={5}>
                <Typography variant="h6" gutterBottom>目標社群</Typography>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  maxHeight: 300, 
                  overflow: 'auto',
                  p: 1
                }}>
                  {communities.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      載入中...
                    </Typography>
                  ) : filteredCommunities.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      該學年度沒有社群
                    </Typography>
                  ) : (
                    filteredCommunities.map((community) => (
                      <Box key={community.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Checkbox
                          checked={selectedCommunities.includes(community.id)}
                          onChange={() => handleCommunityToggle(community.id)}
                        />
                        <Box sx={{ ml: 1, flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {community.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {community.year} - {community.semester}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* 派發預覽 */}
          {selectedCommunities.length > 0 && selectedLessonPackages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                派發預覽 ({selectedCommunities.length} 個社群 × {selectedLessonPackages.length} 個備課包 = {selectedCommunities.length * selectedLessonPackages.length} 個課程)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>備課包</TableCell>
                      <TableCell>學科/年級</TableCell>
                      {filteredCommunities
                        .filter(c => selectedCommunities.includes(c.id))
                        .map(community => (
                          <TableCell key={community.id} align="center">
                            {community.name}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lessonPackages
                      .filter(lp => selectedLessonPackages.includes(lp.id))
                      .map(lessonPackage => (
                        <TableRow key={lessonPackage.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {lessonPackage.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {lessonPackage.subject} / {lessonPackage.grade}
                            </Typography>
                          </TableCell>
                          {filteredCommunities
                            .filter(c => selectedCommunities.includes(c.id))
                            .map(community => {
                              const title = `${community.name} - ${lessonPackage.name}`;
                              return (
                                <TableCell key={community.id} align="center">
                                  <Chip 
                                    label="將派發" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    title={title}
                                  />
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* 派發統計 */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>派發統計：</strong>
                  將建立 {selectedCommunities.length * selectedLessonPackages.length} 個課程派發記錄
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>涉及社群：</strong>
                  {filteredCommunities
                    .filter(c => selectedCommunities.includes(c.id))
                    .map(c => c.name)
                    .join('、')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>涉及備課包：</strong>
                  {lessonPackages
                    .filter(lp => selectedLessonPackages.includes(lp.id))
                    .map(lp => lp.name)
                    .join('、')}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} disabled={loading}>
          取消
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
        >
          {loading ? '派發中...' : '確認派發'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 分組管理對話框組件
function GroupManagementDialog({ open, dispatchRecord, onClose, onSuccess }) {
  const [groups, setGroups] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [draggedStudent, setDraggedStudent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // 取得課程任務
  const fetchCourseTasks = async () => {
    if (!dispatchRecord?.id) return [];
    try {
      const res = await axiosInstance.get(`/api/application-tasks/course-dispatch/${dispatchRecord.id}`);
      return res.data?.data || [];
    } catch (e) {
      console.error('獲取課程任務失敗:', e);
      return [];
    }
  };

  // 將任務派送到指定分組（若未包含）
  const ensureTasksAssignedToGroup = async (groupId) => {
    const tasks = await fetchCourseTasks();
    for (const t of tasks) {
      const assigned = Array.isArray(t.assigned_groups) ? t.assigned_groups : [];
      if (!assigned.includes(groupId)) {
        const newAssigned = [...new Set([...assigned, groupId])];
        try {
          await axiosInstance.put(`/api/application-tasks/${t.id}/assign`, {
            assigned_groups: newAssigned
          });
        } catch (e) {
          console.warn('派送任務到分組失敗:', { taskId: t.id, groupId, error: e.response?.data || e.message });
        }
      }
    }
  };

  // 將所有任務派送到提供的所有分組ID
  const assignAllTasksToGroups = async (groupIds) => {
    const tasks = await fetchCourseTasks();
    for (const t of tasks) {
      const current = Array.isArray(t.assigned_groups) ? t.assigned_groups : [];
      const merged = [...new Set([...current, ...groupIds])];
      try {
        await axiosInstance.put(`/api/application-tasks/${t.id}/assign`, {
          assigned_groups: merged
        });
      } catch (e) {
        console.warn('批量派送任務失敗:', { taskId: t.id, error: e.response?.data || e.message });
      }
    }
  };

  // 獲取課程學生資料（從社群學生清單中提取）
  const fetchClassStudents = async () => {
    if (!dispatchRecord?.Class?.id) return;
    
    try {
      setLoading(true);
      
      // 從社群學生清單獲取
      const response = await axiosInstance.get(`${config.getClassStudentsNew}${dispatchRecord.Class.id}`);
      console.log('社群學生回應:', response.data);
      
      if (response.data && response.data.success) {
        const formattedStudents = response.data.data.map(student => ({
          id: student.student_id || student.id,
          name: student.Student?.name || student.student_name,
          email: student.Student?.email || student.student_email,
          account: student.Student?.email || student.student_email
        }));
        console.log('格式化的學生資料:', formattedStudents);
        setAllStudents(formattedStudents);
      } else {
        setAllStudents([]);
      }
    } catch (error) {
      console.error('獲取學生資料錯誤:', error);
      setAllStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // 獲取課程分組資料
  const fetchGroups = async () => {
    if (!dispatchRecord?.id) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${config.getCourseDispatchGroups}${dispatchRecord.id}/groups`);
      console.log('課程分組 API 回應:', response.data);
      if (response.data && response.data.success) {
        // 處理分組數據，提取 GroupMember 的 user_id
        const groupsData = response.data.data.map(group => ({
          ...group,
          userId: group.GroupMembers ? group.GroupMembers.map(gm => gm.user_id) : []
        }));
        console.log('處理後的課程分組資料:', groupsData);
        setGroups(groupsData);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('獲取課程分組資料錯誤:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // 初始化分組資料
  useEffect(() => {
    if (open && dispatchRecord) {
      fetchClassStudents();
      fetchGroups();
    }
  }, [open, dispatchRecord]);

  // 套用預設分組（從社群複製到課程）
  const handleApplyDefaultGroups = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`${config.applyCourseDefaultGroups}${dispatchRecord.id}/apply-default`);
      if (response.data.success) {
        await fetchGroups(); // 重新獲取課程分組資料
        setSnackbar({ 
          open: true, 
          message: response.data.message || '已套用預設分組設定', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('套用預設分組錯誤:', error);
      const errorMessage = error.response?.data?.message || '套用預設分組失敗';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroup = (group) => {
    // 保留原先的名稱
    setEditingGroup({ 
      ...group, 
      name: group.groupName || group.name,
      groupName: group.groupName || group.name
    });
  };

  // 儲存分組名稱
  const handleSaveGroup = async () => {
    if (!editingGroup) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.put(`${config.updateGroup}${editingGroup.id}`, {
        groupName: editingGroup.groupName || editingGroup.name
      });
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組資料
        setEditingGroup(null);
        setSnackbar({ 
          open: true, 
          message: '分組名稱已更新', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('更新分組錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '更新分組失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingGroup(null);
  };

  // 刪除分組
  const handleDeleteGroup = async (groupId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`${config.deleteGroup}${groupId}`);
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組資料
        setSnackbar({ 
          open: true, 
          message: '分組已刪除', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('刪除分組錯誤:', error);
      const backendMsg = error?.response?.data?.message || '';
      // 直接顯示後端的錯誤訊息，後端會返回詳細的說明
      setSnackbar({ 
        open: true, 
        message: backendMsg || '刪除分組失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 新增分組
  const handleAddGroup = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.createGroup, {
        classId: dispatchRecord.Class.id,
        groupName: `第${groups.length + 1}組`,
        type: 'activity',
        activityId: dispatchRecord.id
      });
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組資料
        setSnackbar({ 
          open: true, 
          message: '分組已新增', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('新增分組錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '新增分組失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignStudents = (group) => {
    setSelectedGroup(group);
    setOpenAssignDialog(true);
  };

  // 點擊學生選擇分組（如果已選擇則取消選擇）
  const handleStudentClick = (student) => {
    if (selectedStudent?.id === student.id) {
      setSelectedStudent(null);
    } else {
      setSelectedStudent(student);
    }
  };

  // 拖曳開始
  const handleDragStart = (student) => {
    setDraggedStudent(student);
  };

  // 拖曳結束
  const handleDragEnd = () => {
    setDraggedStudent(null);
  };

  // 將選中的學生分配到指定分組（或取消分配）
  const handleAssignToGroup = async (groupId) => {
    const student = draggedStudent || selectedStudent;
    if (!student) return;
    
    // 檢查學生是否已經在該組中
    const group = groups.find(g => g.id === groupId);
    const isAlreadyInGroup = group?.userId?.includes(student.id);
    
    if (isAlreadyInGroup) {
      // 如果已經在該組中，則取消分配
      await handleRemoveStudentFromGroup(groupId, student.id);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.assignStudentToGroup, {
        groupId: groupId,
        userId: student.id
      });
      
      if (response.data.success) {
        await fetchGroups();
        setSnackbar({ 
          open: true, 
          message: `已將 ${student.name} 分配到該組`, 
          severity: 'success' 
        });
        setSelectedStudent(null);
        setDraggedStudent(null);
      }
    } catch (error) {
      console.error('分配學生錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || '分配學生失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 從分組中移除學生
  const handleRemoveStudentFromGroup = async (groupId, studentId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.removeStudentFromGroup, {
        groupId: groupId,
        userId: studentId
      });
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組資料
        setSnackbar({ 
          open: true, 
          message: '學生已從分組中移除', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('移除學生錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '移除學生失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 分配學生到分組
  const handleAssignStudentToGroup = async (studentId) => {
    if (!selectedGroup) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.assignStudentToGroup, {
        groupId: selectedGroup.id,
        userId: studentId
      });
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組資料
        setSnackbar({ 
          open: true, 
          message: '學生已分配到分組', 
          severity: 'success' 
        });
        setOpenAssignDialog(false); // 關閉分配對話框
      }
    } catch (error) {
      console.error('分配學生錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '分配學生失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getStudentById = (studentId) => {
    const student = allStudents.find(s => String(s.id) === String(studentId));
    if (!student) {
      console.log('找不到學生:', studentId, '所有學生:', allStudents);
    }
    return student;
  };

  const getUnassignedStudents = () => {
    const assignedStudentIds = groups.flatMap(g => g.userId || []).map(id => String(id));
    return allStudents.filter(s => !assignedStudentIds.includes(String(s.id)));
  };

  const handleConfirmAction = () => {
    if (confirmAction) {
      confirmAction();
    }
  };


  if (!dispatchRecord) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GroupIcon sx={{ color: '#2b687f' }} />
            <Typography variant="h6">{dispatchRecord.title} - 分組管理</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              點擊學生後，再點擊分組區域來分配學生。
            </Typography>
            {groups.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                尚未建立分組，請先套用預設分組設定
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                className="btn-secondary"
                onClick={handleApplyDefaultGroups}
                startIcon={<GroupIcon />}
              >
                套用預設分組
              </Button>
              <Button
                variant="contained"
                className="btn-primary"
                onClick={handleAddGroup}
                startIcon={<AddIcon />}
              >
                新增小組
              </Button>
            </Box>

            {/* 未分配學生列表 */}
            {getUnassignedStudents().length > 0 && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  未分配學生 ({getUnassignedStudents().length}) - 點擊選擇或拖曳到分組
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getUnassignedStudents().map((student) => (
                    <Chip
                      key={student.id}
                      label={student.name}
                      draggable
                      onDragStart={() => handleDragStart(student)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleStudentClick(student)}
                      color={selectedStudent?.id === student.id ? 'primary' : 'default'}
                      variant={selectedStudent?.id === student.id ? 'filled' : 'outlined'}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    />
                  ))}
                </Box>
                {(selectedStudent || draggedStudent) && (
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    已選擇：{(selectedStudent || draggedStudent).name} - 請點擊或拖曳到下方的分組區域來分配（再次點擊分組可取消分配）
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* 分組列表 - 兩組並排顯示 */}
          {groups.length > 0 ? (
            <Grid container spacing={2}>
              {groups.map((group) => {
                const isStudentInGroup = (draggedStudent || selectedStudent) && group.userId?.includes((draggedStudent || selectedStudent)?.id);
                return (
                  <Grid item xs={12} sm={6} key={group.id}>
                    <Box
                      sx={{
                        border: (draggedStudent || selectedStudent) 
                          ? (isStudentInGroup ? '2px solid #f44336' : '2px solid #2b687f')
                          : '2px solid #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s',
                        cursor: (draggedStudent || selectedStudent) ? 'pointer' : 'default',
                        '&:hover': {
                          borderColor: (draggedStudent || selectedStudent) 
                            ? (isStudentInGroup ? '#f44336' : '#2b687f')
                            : '#2b687f',
                          boxShadow: (draggedStudent || selectedStudent) 
                            ? '0 4px 12px rgba(0,0,0,0.15)' 
                            : '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAssignToGroup(group.id);
                      }}
                      onClick={() => (draggedStudent || selectedStudent) && handleAssignToGroup(group.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2b687f' }}>
                          {editingGroup?.id === group.id ? (
                            <TextField
                              value={editingGroup.groupName || editingGroup.name}
                              onChange={(e) => setEditingGroup({ ...editingGroup, groupName: e.target.value, name: e.target.value })}
                              size="small"
                              variant="outlined"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              {group.groupName || group.name}
                              <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: '8px' }}>
                                ({(group.userId || []).length} 人)
                              </span>
                            </>
                          )}
                        </Typography>
                        {editingGroup?.id === group.id ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              sx={{ color: '#2b687f' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveGroup();
                              }}
                              title="確認"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEdit();
                              }}
                              title="取消"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              sx={{ color: '#2b687f' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditGroup(group);
                              }}
                              title="編輯名稱"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: '#f44336' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGroup(group.id);
                              }}
                              title="刪除小組"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      
                      <Box 
                        sx={{ 
                          minHeight: '80px',
                          border: '2px dashed #ccc',
                          borderRadius: 1,
                          p: 1.5,
                          backgroundColor: '#fafafa',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1
                        }}
                      >
                        {(group.userId || []).map((studentId) => {
                          const student = getStudentById(studentId);
                          return student ? (
                            <Chip 
                              key={studentId}
                              label={student.name} 
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleRemoveStudentFromGroup(group.id, studentId);
                              }}
                              sx={{ 
                                backgroundColor: '#e3f2fd',
                                '& .MuiChip-deleteIcon': {
                                  color: '#1976d2',
                                  '&:hover': { color: '#d32f2f' }
                                }
                              }}
                            />
                          ) : null;
                        })}
                        {(group.userId || []).length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', width: '100%', textAlign: 'center', py: 2 }}>
                            {(draggedStudent || selectedStudent) 
                              ? (isStudentInGroup 
                                  ? `點擊取消 ${(draggedStudent || selectedStudent).name} 的分配`
                                  : `點擊或拖曳將 ${(draggedStudent || selectedStudent).name} 分配到此組`)
                              : '拖曳學生到此處或點擊學生來分配'}
                          </Typography>
                        )}
                        {isStudentInGroup && (group.userId || []).length > 0 && (
                          <Typography variant="caption" color="error" sx={{ width: '100%', display: 'block', mt: 0.5 }}>
                            再次點擊可取消分配 {(draggedStudent || selectedStudent).name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Alert severity="info">
              尚未建立分組，請先套用預設分組設定
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose}>
            關閉
          </Button>
        </DialogActions>
      </Dialog>

      {/* 分配學生對話框 */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ color: '#2b687f' }} />
            <Typography variant="h6">分配學生到 {selectedGroup?.name}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            可分配的學生 ({getUnassignedStudents().length})
          </Typography>
          <List>
            {getUnassignedStudents().map((student) => (
              <ListItem key={student.id}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#2b687f' }}>
                    {student.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={student.name}
                  secondary={student.account}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleAssignStudentToGroup(student.id)}
                >
                  分配
                </Button>
              </ListItem>
            ))}
            {getUnassignedStudents().length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                所有學生都已分配到小組
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setOpenAssignDialog(false)}>
            關閉
          </Button>
        </DialogActions>
      </Dialog>


      {/* 確認對話框 */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>確認操作</DialogTitle>
        <DialogContent>
          <Typography>確定要修改分組名稱嗎？</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setShowConfirmDialog(false)}>
            取消
          </Button>
          <Button onClick={handleConfirmAction} variant="contained">
            確定
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
